import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type Chart,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import { cn } from "~/lib/utils";
import { EVAL_CHART_HEIGHT_CLS, SCORE_BAND_HEX, TREND_AXIS_TEXT_COLOR } from "./constants/charts";
import { SCORE_BAND_CONFIG } from "./constants/scoring";
import type { ScoreBucket } from "./selectors/runs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

/**
 * The count at the end of each bar. Four bars is few enough to label all of them,
 * and an x-axis to measure integers this small against is strictly worse.
 */
const barCountPlugin = (color: string): Plugin<"bar"> => ({
  id: "evalDistributionCounts",
  afterDatasetsDraw(chart: Chart<"bar">) {
    const { ctx } = chart;
    ctx.save();
    ctx.font = "600 11px system-ui, -apple-system, sans-serif";
    ctx.fillStyle = color;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    chart.getDatasetMeta(0).data.forEach((element, index) => {
      const value = chart.data.datasets[0].data[index];
      if (typeof value === "number") {
        ctx.fillText(String(value), element.x + 6, element.y);
      }
    });
    ctx.restore();
  },
});

const chartOptions = (axisTextColor: string, maxCount: number): ChartOptions<"bar"> => ({
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  // Room on the right for the count labels, which are drawn outside the bar.
  layout: { padding: { right: 28 } },
  plugins: {
    // Every bar is named on the axis and its fill is legended by the matrix's
    // own score ramp, so a legend box would add a fourth statement of identity.
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: {
        label: (item) => `${item.parsed.x} ${item.parsed.x === 1 ? "case" : "cases"}`,
      },
    },
  },
  scales: {
    x: {
      // A count axis has to start at zero, and `suggestedMax` keeps a suite
      // where every case scored well from drawing one bar the full width.
      min: 0,
      suggestedMax: Math.max(maxCount, 1),
      ticks: { display: false },
      grid: { display: false },
      border: { display: false },
    },
    y: {
      ticks: { color: axisTextColor, font: { size: 11 } },
      grid: { display: false },
      border: { display: false },
    },
  },
});

/**
 * How many of the run's cases landed in each rubric band. Fills are the matrix's
 * own score ramp, so a bar's colour and a cell's mean the same thing. Counted per
 * case, so a case run five times does not outweigh one run once.
 */
export const ScoreDistribution = ({ buckets }: { buckets: ScoreBucket[] }) => {
  const isDark = useIsDarkMode();
  const mode = isDark ? "dark" : "light";
  const axisTextColor = TREND_AXIS_TEXT_COLOR[mode];
  const total = buckets.reduce((sum, bucket) => sum + bucket.caseCount, 0);

  if (total === 0) {
    return (
      <div
        className={cn(
          EVAL_CHART_HEIGHT_CLS,
          "flex items-center justify-center text-sm text-muted-foreground"
        )}
      >
        No scored cases in this run yet.
      </div>
    );
  }

  return (
    <div className={EVAL_CHART_HEIGHT_CLS}>
      <Bar
        data={{
          labels: buckets.map((bucket) => SCORE_BAND_CONFIG[bucket.band].label),
          datasets: [
            {
              label: "Cases",
              data: buckets.map((bucket) => bucket.caseCount),
              backgroundColor: buckets.map((bucket) => SCORE_BAND_HEX[mode][bucket.band]),
              // Rounded data-end, anchored to the baseline.
              borderRadius: 4,
              borderSkipped: "start",
              barPercentage: 0.6,
            },
          ],
        }}
        options={chartOptions(axisTextColor, Math.max(...buckets.map((b) => b.caseCount)))}
        plugins={[barCountPlugin(axisTextColor)]}
      />
    </div>
  );
};
